import React, { useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import './ResponsiveOverlay.css';
import '../ModalOverlay/ModalOverlay.css';
import MobileSheet from './MobileSheet';
import DesktopDialog from './DesktopDialog';
import {
  useOverlayPresentation,
  type OverlayPresentation,
} from '@/hooks/useOverlayPresentation';
import { useNativeSheetFeedback } from '@/hooks/useNativeSheetFeedback';
import { useKeyboardAwareContainer } from '@/hooks/useKeyboardAwareContainer';
import { useIsMobileViewport } from '@/hooks/useIsMobileViewport';
import { blurActiveEditableElement } from '@/utils/keyboardInset';
import { warn } from '@/utils/logger';

export type { OverlayPresentation };

export interface ResponsiveOverlayProps {
  children?: React.ReactNode;
  onClose?: () => void;
  isClosing?: boolean;
  animate?: boolean;
  /** Layout mode — default `sheet` (bottom sheet on mobile, centered on desktop). */
  presentation?: OverlayPresentation;
  position?: 'center' | 'right-side';
  className?: string;
  /** Panel class forwarded to MobileSheet / DesktopDialog shell. */
  panelClassName?: string;
  /** When true (default on mobile viewport), focus scroll runs inside keyboard scroll regions. */
  keyboardAware?: boolean;
  /** Hide drag handle on mobile sheet. */
  showSheetHandle?: boolean;
  /** Skip auto shell wrap — children include their own sheet/dialog chrome. */
  unwrapped?: boolean;
  /** Force bottom-sheet layout (e.g. Capacitor native where matchMedia may not match mobile). */
  forceMobileSheet?: boolean;
  /** Portal to document.body (default true). */
  portal?: boolean;
  role?: string;
  'aria-label'?: string;
}

const ResponsiveOverlay: React.FC<ResponsiveOverlayProps> = ({
  children,
  onClose,
  isClosing = false,
  animate = false,
  presentation = 'sheet',
  position = 'center',
  className = '',
  panelClassName = '',
  keyboardAware,
  showSheetHandle = true,
  unwrapped = false,
  forceMobileSheet = false,
  portal = true,
  role,
  'aria-label': ariaLabel,
}) => {
  const isMobileViewport = useIsMobileViewport();
  const resolvedKeyboardAware = keyboardAware ?? isMobileViewport;
  const layout = useOverlayPresentation(presentation);
  const useMobileSheet = layout.useMobileSheet || forceMobileSheet;
  const useCenteredDialog =
    !useMobileSheet &&
    (presentation === 'dialog' || presentation === 'sheet');

  useNativeSheetFeedback(useMobileSheet);

  const { containerRef, setContainerRef, blurActiveInput } = useKeyboardAwareContainer({
    enabled: resolvedKeyboardAware && !useMobileSheet,
  });

  useEffect(() => {
    if (!isMobileViewport && !forceMobileSheet) return;
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = originalOverflow;
    };
  }, [isMobileViewport, forceMobileSheet]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Esc') {
        blurActiveInput();
        blurActiveEditableElement();
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, blurActiveInput]);

  const handleBackdropPointer = (e: React.PointerEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('.MuiPickersPopper-root') || target.closest('.MuiPopper-root')) {
      return;
    }
    if (e.target === containerRef.current) {
      blurActiveInput();
      blurActiveEditableElement();
      onClose?.();
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const overlays = document.querySelectorAll('.responsive-overlay');
    if (overlays.length > 1) {
      warn('[ResponsiveOverlay] Multiple overlays detected:', overlays.length);
    }
  }, [children]);

  const overlayClassName = useMemo(
    () =>
      clsx(
        'responsive-overlay modal-overlay',
        useMobileSheet && 'responsive-overlay--mobile-sheet responsive-overlay--sheet',
        forceMobileSheet && 'responsive-overlay--forced-mobile-sheet',
        useCenteredDialog && 'responsive-overlay--dialog responsive-overlay--sheet',
        presentation === 'dialog' && layout.isMobileViewport && 'responsive-overlay--mobile-dialog',
        layout.useFullscreen && 'responsive-overlay--fullscreen',
        layout.usePopover && 'responsive-overlay--popover',
        resolvedKeyboardAware && 'responsive-overlay--keyboard-aware modal-overlay--keyboard-aware',
        animate && 'animate',
        isClosing && 'closing',
        position === 'right-side' && 'responsive-overlay--right-side modal-overlay--right-side',
        className,
      ),
    [
      useMobileSheet,
      forceMobileSheet,
      useCenteredDialog,
      layout.useFullscreen,
      layout.usePopover,
      layout.isMobileViewport,
      presentation,
      resolvedKeyboardAware,
      animate,
      isClosing,
      position,
      className,
    ],
  );

  const wrappedChildren = useMemo(() => {
    if (unwrapped || layout.usePopover || layout.useFullscreen) {
      if (useMobileSheet && showSheetHandle && unwrapped) {
        return (
          <>
            <div className="mobile-sheet__handle" aria-hidden="true">
              <div className="mobile-sheet__handle-pill" />
            </div>
            {children}
          </>
        );
      }
      return children;
    }
    if (useMobileSheet) {
      return (
        <MobileSheet
          className={panelClassName}
          showHandle={showSheetHandle}
          role={role}
          aria-label={ariaLabel}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </MobileSheet>
      );
    }
    if (useCenteredDialog) {
      return (
        <DesktopDialog
          className={panelClassName}
          role={role}
          aria-label={ariaLabel}
          onClick={(e) => e.stopPropagation()}
        >
          {children}
        </DesktopDialog>
      );
    }
    return children;
  }, [
    unwrapped,
    layout.usePopover,
    layout.useFullscreen,
    useMobileSheet,
    useCenteredDialog,
    panelClassName,
    showSheetHandle,
    role,
    ariaLabel,
    children,
  ]);

  const overlay = (
    <div
      className={overlayClassName}
      ref={setContainerRef}
      onPointerDown={handleBackdropPointer}
    >
      {wrappedChildren}
    </div>
  );

  if (portal && typeof document !== 'undefined') {
    return createPortal(overlay, document.body);
  }

  return overlay;
};

export default ResponsiveOverlay;
